// TODO: Overhaul — delete this file (and every *.mock.ts it composes) at cut-over. The single mock
// `ACCOUNT_OVERHAUL_CLIENT` for the overhaul screens: it routes each overhaul path to its per-resource
// handler, backed by per-entity in-memory state seeded from the live legacy endpoints. Provided once as a
// root singleton (flag-gated on the `accountOverhaul` build flag), so every screen and dialog shares one
// mocked backend — a create / close / withdrawal on one screen is visible on the next, and query
// invalidation just re-reads it. Cut-over is deleting `provideOverhaulMockBackend()` + the mock files; the
// queries then hit the real, root-provided client unchanged.
//
// Lifetime caveat (mock-only): the per-entity stores live on this root singleton, which is never torn down.
// `queryClient.clear()` on logout empties the TanStack cache but NOT these in-memory Maps, so a within-session
// logout → login keeps any mutated entity's store. Harmless in practice — this is dev/staging-ao only, and
// entity ids are globally unique so a different user can't address a previous user's store — a full reload
// reseeds from the live endpoints. The real root-provided client has no such state, so this vanishes at cut-over.
import { Client } from 'openapi-fetch';

import { Provider } from '@angular/core';
import { Router } from '@angular/router';

import { createInvestmentsMockClient } from '@views/investor/investments-ao/queries/investments.mock';
import { createPortfolioOverTimeMockClient } from '@views/investor/investments-ao/queries/portfolio-over-time.mock';
import { createPortfolioSummaryMockClient } from '@views/investor/investments-ao/queries/portfolio-summary.mock';
import { createAutoRulesMockClient } from '@views/investor/move-and-invest/queries/auto-rules.mock';
import { createMovesMockClient } from '@views/investor/move-and-invest/queries/moves.mock';
import { createOrdersMockClient } from '@views/investor/offer/queries/offer.mock';
import { createDashboardInvestorMockClient } from '@views/investor/queries/dashboard-investor/dashboard-investor.mock';
import { createEntityMockClient } from '@views/investor/queries/entity/entity.mock';
import { createInvestmentAccountTransactionsMockClient } from '@views/investor/queries/investment-account-transactions/investment-account-transactions.mock';
import { createInvestmentAccountsMockClient } from '@views/investor/queries/investment-accounts/investment-accounts.mock';
import { createInvestmentRatesMockClient } from '@views/investor/queries/investment-rates/investment-rates.mock';
import { createManagedFundMockClient } from '@views/investor/queries/managed-fund/managed-fund.mock';

import {
  SquirrelMoneyCoreContractsCreateMoveForm as CreateMoveForm,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';
import { paths as api1Paths } from '@core/schemas/api1-schema';
import { paths as api2Paths } from '@core/schemas/api2-schema';
import { isFeatureEnabled } from '@core/utilities/feature-flags';
import {
  ACCOUNT_OVERHAUL_CLIENT,
  API1_CLIENT,
  API2_CLIENT,
} from '@core/utilities/networkLayer/openapi-fetch.providers';

type LooseVerb = (path: string, init?: unknown) => Promise<unknown>;
interface LooseClient {
  GET: LooseVerb;
  POST: LooseVerb;
  PUT: LooseVerb;
  DELETE: LooseVerb;
}

/** The create-move POST shape the seam reads to reflect a move in the accounts store. */
interface MoveCreateInit {
  params?: { path?: { investorEntityId?: number } };
  body?: CreateMoveForm;
}

/** The created move the seam reads/rewrites: its `moveId` (to key the in-flight sale), the real account names,
 * plus the immediate/sale split (the consumer `MoveDto` doesn't carry the split, so the mock writes it on for the
 * "stop moving funds" summary). */
interface MoveMutableFields {
  moveId?: number;
  fromInvestmentAccountName?: string | null;
  toInvestmentAccountName?: string | null;
  immediateAmount?: number;
  saleAmount?: number;
}

/**
 * Compose the per-resource mock handlers into one client and route each path to its handler. Order is
 * most-specific first: `/investment-accounts/{id}/moves/active` and `/investment-accounts/{id}/transactions`
 * must reach the moves / transactions handlers, not the accounts handler. Every stateful handler keys its
 * store by the request path's `investorEntityId`; `getCurrentEntityId` serves only the one user-scoped
 * endpoint (`/v1/dashboard/investor`), whose path carries no entity.
 */
export function createOverhaulMockClient(deps: {
  api1: Client<api1Paths>;
  api2: Client<api2Paths>;
  getCurrentEntityId: () => number | null;
}): Client<accountOverhaulPaths> {
  const { api1, api2, getCurrentEntityId } = deps;
  // Keep the typed accounts mock so its `applyMove` hook is reachable (a created move updates the accounts store).
  const accountsMock = createInvestmentAccountsMockClient({ api1, api2 });
  const accounts = accountsMock as unknown as LooseClient;
  const entity = createEntityMockClient({ api1, api2 }) as unknown as LooseClient;
  const moves = createMovesMockClient() as unknown as LooseClient;
  const transactions = createInvestmentAccountTransactionsMockClient({
    api1,
    api2,
    accountOf: accountsMock.accountOf,
  }) as unknown as LooseClient;
  const rates = createInvestmentRatesMockClient({ api2 }) as unknown as LooseClient;
  const dashboard = createDashboardInvestorMockClient({
    api2,
    getEntityId: getCurrentEntityId,
  }) as unknown as LooseClient;
  const portfolioOverTime = createPortfolioOverTimeMockClient() as unknown as LooseClient;
  const portfolioSummary = createPortfolioSummaryMockClient({ api1 }) as unknown as LooseClient;
  const managedFunds = createManagedFundMockClient({ api1 }) as unknown as LooseClient;
  const offers = createOrdersMockClient() as unknown as LooseClient;
  const autoRules = createAutoRulesMockClient({ accountsOf: accountsMock.accountsOf }) as unknown as LooseClient;
  const investments = createInvestmentsMockClient({
    api1,
    accountsOf: accountsMock.accountsOf,
  }) as unknown as LooseClient;

  const pick = (path: string): LooseClient => {
    if (path === '/v1/investments/entities/{entityId}') return investments;
    if (path.includes('/reporting/portfolio-over-time')) return portfolioOverTime;
    if (path.includes('/reporting/portfolio')) return portfolioSummary;
    if (path.includes('/managed-funds/valuations')) return managedFunds;
    if (path.includes('/managed-funds/settings/investment-plan')) return managedFunds;
    if (path.includes('/managed-funds/') && path.endsWith('/redemptions')) return managedFunds;
    if (path.endsWith('/investment-orders')) return offers;
    if (path.includes('/investment-offers/') && path.endsWith('/sell/details')) return offers;
    if (path.includes('/investment-offers/') && (path.endsWith('/confirm') || path.endsWith('/decline'))) return offers;
    if (path.includes('/investment-account-rules')) return autoRules;
    if (path.includes('/moves')) return moves;
    if (path.includes('/transactions')) return transactions;
    if (path.includes('/investment-accounts')) return accounts;
    if (path.includes('/dashboard/investor')) return dashboard;
    if (path.includes('/investments/rates')) return rates;
    // Bare entity read + withdrawals.
    if (path.includes('/investor-entities')) return entity;
    throw new Error(`createOverhaulMockClient: unhandled ${path}`);
  };

  return {
    GET: (path: string, init?: unknown) => pick(path).GET(path, init),
    POST: async (path: string, init?: unknown) => {
      const result = await pick(path).POST(path, init);
      const failed = (result as { error?: unknown }).error;
      // A created move (POST `/moves`, not a `/moves/{id}/cancel`) shifts funds in the accounts store: the
      // immediate portion now, the sale portion over time. Only on a successful create.
      if (path.endsWith('/moves') && !failed) {
        const { params, body } = (init ?? {}) as MoveCreateInit;
        const entityId = params?.path?.investorEntityId;
        const move = (result as { data?: MoveMutableFields | null }).data;
        if (
          entityId != null &&
          move != null &&
          body?.fromInvestmentAccountId != null &&
          body?.toInvestmentAccountId != null
        ) {
          const split = accountsMock.applyMove(
            entityId,
            body.fromInvestmentAccountId,
            body.toInvestmentAccountId,
            body.amount ?? 0,
            move.moveId ?? 0,
          );
          // The moves mock stubs account names as `Account {id}` and can't know the immediate/sale split (that
          // depends on the source's balance); rewrite the created move with the real names + the split from the
          // accounts store, so the "move underway" panel reads e.g. "Retirement" (not "Account 4") and the "stop
          // moving funds" summary has the breakdown. It's the same object the moves store holds, so a later
          // GET /moves/active reflects it too.
          move.fromInvestmentAccountName =
            accountsMock.accountNameOf(entityId, body.fromInvestmentAccountId) ?? move.fromInvestmentAccountName;
          move.toInvestmentAccountName =
            accountsMock.accountNameOf(entityId, body.toInvestmentAccountId) ?? move.toInvestmentAccountName;
          move.immediateAmount = split.immediateAmount;
          move.saleAmount = split.saleAmount;
        }
      }
      // A cancelled move (POST `.../{moveId}/cancel`) freezes its in-flight sale, so the source keeps its unsold
      // funds and the destination keeps only what had already moved (see stopMoveSale). Only on success.
      if (path.endsWith('/cancel') && !failed) {
        const { params } = (init ?? {}) as { params?: { path?: { investorEntityId?: number; moveId?: number } } };
        const entityId = params?.path?.investorEntityId;
        const moveId = params?.path?.moveId;
        if (entityId != null && moveId != null) accountsMock.stopMoveSale(entityId, moveId);
      }
      return result;
    },
    PUT: (path: string, init?: unknown) => pick(path).PUT(path, init),
    DELETE: (path: string, init?: unknown) => pick(path).DELETE(path, init),
  } as unknown as Client<accountOverhaulPaths>;
}

/** The current investor entity id from the active URL (`…/entities/{id}/…`), or `null` if none is active. */
function entityIdFromUrl(url: string): number | null {
  const match = url.match(/\/entities\/(\d+)/);
  return match ? Number(match[1]) : null;
}

/**
 * Root provider that overrides `ACCOUNT_OVERHAUL_CLIENT` with the single mock backend — but ONLY when the
 * `accountOverhaul` build flag is on (dev + staging-ao; off in prod/staging, where the real client stands).
 * Add after `openApiFetchProviders` in the app's root providers so it wins the token when the flag is on.
 * Provided at root → one singleton instance, shared by every screen and dialog. Delete at cut-over.
 */
export function provideOverhaulMockBackend(): Provider[] {
  if (!isFeatureEnabled('accountOverhaul')) return [];
  return [
    {
      provide: ACCOUNT_OVERHAUL_CLIENT,
      useFactory: (api1: Client<api1Paths>, api2: Client<api2Paths>, router: Router) =>
        createOverhaulMockClient({ api1, api2, getCurrentEntityId: () => entityIdFromUrl(router.url) }),
      deps: [API1_CLIENT, API2_CLIENT, Router],
    },
  ];
}
